import { AxiosError } from 'axios'
import {
  ApiResponse,
  ErrorResponse,
  Dictionary,
  ResponseHandlerParams,
  ApiError,
  ApiResult,
  ErrorField,
  ResponseData,
  SnackbarFunction,
  CallbackFunction,
  SetErrorFunction,
  TranslationFunction,
  SnackbarOptions
} from './types/api'

// Re-export Dictionary for backward compatibility
export type { Dictionary } from './types/api'

// Type guard to check if response has a 'response' property (ErrorResponse)
function isErrorResponse(
  response: ApiResponse | ErrorResponse | undefined
): response is ErrorResponse {
  return response !== undefined && 'response' in response
}

/**
 * Handles API responses uniformly across the application
 * Processes successful and error responses, displaying appropriate messages
 */
export const responseHandler = async ({
  response,
  snackbar,
  dictionary,
  callback,
  msgSuccess,
  callbackError,
  setError,
  hideErrorMsg = false,
  t // i18next dictionary
}: ResponseHandlerParams): Promise<void> => {
  const status = isErrorResponse(response)
    ? response.status || response.response?.status
    : response?.status

  const data = isErrorResponse(response)
    ? response.data || response.response?.data
    : response?.data

  if (status === 200 || status === 201 || status === 204) {
    if (callback && data) {
      await callback(data)
    }
    if (msgSuccess && snackbar) {
      snackbar(
        typeof msgSuccess === 'string'
          ? msgSuccess
          : JSON.stringify(msgSuccess),
        { variant: 'success' }
      )
    }
  } else if (hideErrorMsg) {
    if (callbackError) {
      callbackError()
    }
    return
  } else {
    if (callbackError) {
      callbackError()
    }

    let errMessage = ''

    if (data?.errors) {
      if (setError) {
        data.errors.forEach(e => setError(e.field, { message: e.message }))
      }
      errMessage = data.errors[0]?.message || ''
    }

    const error400msg = dictionary
      ? `${dictionary.error} - ${dictionary.bad_request}`
      : `${t ? t('error') : 'Error'} - ${t ? t('bad_request') : 'Bad Request'}`

    const error401msg = dictionary
      ? `${dictionary.error} - ${dictionary.not_authorized}`
      : `${t ? t('error') : 'Error'} - ${
          t ? t('not_authorized') : 'Not Authorized'
        }`

    const error403msg = dictionary
      ? `${dictionary.error} - ${dictionary.permission_error}`
      : `${t ? t('error') : 'Error'} - ${
          t ? t('permission_error') : 'Permission Error'
        }`

    const error404msg = dictionary
      ? `${dictionary.error} - ${dictionary.not_found}`
      : `${t ? t('error') : 'Error'} - ${t ? t('not_found') : 'Not Found'}`

    const error500msg = dictionary
      ? dictionary.server_error
      : t
      ? t('server_error')
      : 'Server Error'

    if (snackbar) {
      if (status === 400) {
        if (errMessage) {
          snackbar(errMessage, {
            variant: 'warning',
            style: { whiteSpace: 'pre-wrap' },
            autoHideDuration: 5000
          })
        } else {
          snackbar(error400msg, { variant: 'warning' })
        }
      } else if (status === 401) {
        if (errMessage) {
          snackbar(errMessage, {
            variant: 'warning',
            style: { whiteSpace: 'pre-wrap' },
            autoHideDuration: 5000
          })
        } else {
          snackbar(error401msg, { variant: 'warning' })
        }
      } else if (status === 403) {
        snackbar(error403msg, { variant: 'warning' })
      } else if (status === 404) {
        snackbar(error404msg, { variant: 'warning' })
      } else if (status === 503) {
        return
      } else {
        snackbar(error500msg, { variant: 'error' })
      }
    }
  }
}

export const errorHandler = (error: AxiosError): ApiResult<never> => {
  if (error.response) {
    return {
      error: {
        status: error.response.status,
        message: error.message,
        details: error.response.data
      }
    }
  }

  return {
    error: {
      status: 500,
      message: error.message || 'Unknown error'
    }
  }
}
